import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  useLoad,
  useTriggerLoad,
  useTriggerLoadWithInput,
} from "@redotech/react-util/load";
import {
  FulfillmentGroupData,
  FulfillmentGroupPickStatus,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  expandLineItemsToPickableItems,
  FulfillmentOrderLineItem,
  getPickedMapByExternalId,
} from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { RedoFulfillmentTote } from "@redotech/redo-fulfillment-group-common/fulfillment-tote";
import { isWmsSotEnabledForLocation } from "@redotech/redo-fulfillment-wms-common/wms-constants";
import AlertHexagonIcon from "@redotech/redo-icons/alert-hexagon_filled.svg";
import { LocationId } from "@redotech/redo-inventory-id";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ProductId } from "@redotech/redo-product-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoStandaloneTable } from "@redotech/redo-web/arbiter-components/tables/redo-standalone-table";
import { RedoTableColumn } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo, useState } from "react";
import { getDeviceLocationId } from "../../hooks/use-device-location";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { BinQuantityModal } from "../../wms/pick-sessions/bin-quantity-modal";
import type { ProblemPickSource } from "../../wms/pick-sessions/problem-pick-source-allocation";
import {
  groupProblemPickLineItems,
  takeProblemPickSources,
} from "../../wms/pick-sessions/problem-pick-source-allocation";

interface ItemTableRow extends FulfillmentOrderLineItem {
  pickedQuantity: number;
  allOriginalItems: FulfillmentOrderLineItem[];
}

const createItemColumn = (): RedoTableColumn<ItemTableRow> => ({
  key: "item",
  widthMode: { type: "fill", minLength: "200px" },
  renderHeaderCell: () => StandardRedoTableHeaderCell({ headerText: "Item" }),
  renderNormalCell: ({ row }) => (
    <Flex align="center" gap="md" px="xl" py="sm">
      {row.imageUrl && (
        <img
          alt={row.productTitle}
          src={row.imageUrl}
          style={{
            width: "32px",
            height: "32px",
            objectFit: "cover",
            borderRadius: "4px",
          }}
        />
      )}
      <Flex dir="column" gap="xxs">
        <Text fontSize="xs" fontWeight="medium">
          {row.variantTitle !== "Default Title"
            ? `${row.productTitle} - ${row.variantTitle}`
            : row.productTitle}
        </Text>
        <Text fontSize="xs" textColor="tertiary">
          SKU: {row.sku}
        </Text>
      </Flex>
    </Flex>
  ),
});

const createBinLocationColumn = (): RedoTableColumn<ItemTableRow> => ({
  key: "binLocation",
  widthMode: { type: "fixed", width: "120px" },
  renderHeaderCell: () =>
    StandardRedoTableHeaderCell({ headerText: "Bin location" }),
  renderNormalCell: ({ row }) => (
    <RedoTableTextCell text={row.bin || "—"} textColor="tertiary" />
  ),
  alignment: "left",
});

const createMissingColumn = (): RedoTableColumn<ItemTableRow> => ({
  key: "missing",
  widthMode: { type: "fixed", width: "80px" },
  renderHeaderCell: () =>
    StandardRedoTableHeaderCell({ headerText: "Missing" }),
  renderNormalCell: ({ row }) => (
    <Flex
      align="center"
      justify="center"
      p="sm"
      style={{ backgroundColor: "#FEF3F2" }}
      w="full"
    >
      <RedoTableTextCell
        fontWeight="semibold"
        text={String(row.quantity - row.pickedQuantity)}
        textColor="error"
      />
    </Flex>
  ),
  alignment: "center",
});

const createMarkAsPickedColumn = (
  onMarkAsPicked: (row: ItemTableRow) => void,
  disabled?: boolean,
): RedoTableColumn<ItemTableRow> => ({
  key: "markAsPicked",
  widthMode: { type: "fixed", width: "130px" },
  renderHeaderCell: () => StandardRedoTableHeaderCell({ headerText: "" }),
  renderNormalCell: ({ row }) => (
    <Flex align="center" justify="center" p="sm">
      <RedoButton
        disabled={disabled}
        hierarchy="tertiary"
        onClick={() => onMarkAsPicked(row)}
        size="xs"
        text={disabled ? "Assign tote first" : "Mark as picked..."}
        theme="normal"
      />
    </Flex>
  ),
  alignment: "center",
});

export const MissingItemsModal = memo(function MissingItemsModal({
  isOpen,
  fulfillmentGroup,
  onClose,
  onConfirm,
  onViewFullDetails,
  onFulfillmentGroupUpdated,
}: {
  isOpen: boolean;
  fulfillmentGroup: FulfillmentGroupData;
  onClose: () => void;
  onConfirm: () => void;
  onViewFullDetails: () => void;
  onFulfillmentGroupUpdated?: (group: FulfillmentGroupData) => void;
}) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useContext(TeamContext);

  const [isBinQuantityModalOpen, setIsBinQuantityModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItemTableRow | null>(null);
  const [quantityLeftToPick, setQuantityLeftToPick] = useState(0);
  const [pickSessionReloadKey, setPickSessionReloadKey] = useState(0);

  const pickSessionLoad = useLoad(
    async (signal) => {
      if (!fulfillmentGroup?.pickSessionId) {
        return null;
      }
      return await rpcClient.getPickSession(
        { id: fulfillmentGroup.pickSessionId },
        { signal },
      );
    },
    [rpcClient, fulfillmentGroup?.pickSessionId, pickSessionReloadKey],
  );

  const pickSession = pickSessionLoad.value;

  const requireSourceBin = !!(
    isWmsSotEnabledForLocation(
      team?.settings?.outboundLabels?.wms,
      pickSession?.crdbLocationId,
    ) && pickSession?.crdbLocationId
  );

  const hasToteAssigned = useMemo(() => {
    if (!pickSession) {
      return false;
    }
    return pickSession.items.some(
      (item) =>
        item.fulfillmentGroupId === fulfillmentGroup._id &&
        item.quantityByTote &&
        Object.keys(item.quantityByTote).length > 0,
    );
  }, [pickSession, fulfillmentGroup._id]);

  const needsToteAssignment = requireSourceBin && !hasToteAssigned;

  const [selectedTote, setSelectedTote] =
    useState<RedoListItem<RedoFulfillmentTote> | null>(null);
  const [toteSearchString, setToteSearchString] = useState("");

  const availableTotesLoad = useLoad(
    async (signal) => {
      if (!needsToteAssignment) {
        return [];
      }
      const result = await rpcClient.getTotes(
        { locationId: getDeviceLocationId() },
        { signal },
      );
      return result.totes || [];
    },
    [rpcClient, needsToteAssignment],
  );

  const toteOptions: RedoListItem<RedoFulfillmentTote>[] = useMemo(() => {
    if (!availableTotesLoad.value) {
      return [];
    }
    const currentPickSessionId = pickSession?._id?.toString();
    const filtered = toteSearchString
      ? availableTotesLoad.value.filter((t) =>
          t.name.toLowerCase().includes(toteSearchString.toLowerCase()),
        )
      : availableTotesLoad.value;
    return filtered
      .filter(
        (t) => !t.pickSessionId || t.pickSessionId === currentPickSessionId,
      )
      .map((tote) => ({
        type: "text" as const,
        id: tote._id.toString(),
        value: tote,
        text: tote.name,
      }));
  }, [availableTotesLoad.value, toteSearchString, pickSession]);

  const [assignToteLoad, triggerAssignTote] = useTriggerLoad(async (signal) => {
    if (!selectedTote || !fulfillmentGroup._id) {
      return;
    }
    try {
      const result = await rpcClient.moveFulfillmentGroupToTotes(
        {
          fulfillmentGroupId: fulfillmentGroup._id,
          toteIds: [selectedTote.value._id.toString()],
        },
        { signal },
      );
      if (!result.success) {
        toast(result.message || "Failed to assign tote", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 3 }),
        });
        return;
      }
      // Reload pick session to update hasToteAssigned
      setPickSessionReloadKey((k) => k + 1);
      setSelectedTote(null);
      setToteSearchString("");
    } catch (error) {
      toast("Failed to assign tote", {
        variant: "error",
        duration: Temporal.Duration.from({ seconds: 3 }),
      });
    }
  });

  const showCustomAttributes =
    !!team?.settings?.outboundLabels?.degroupItemsByCustomAttributes;

  const pickedQuantityByExternalId = getPickedMapByExternalId(fulfillmentGroup);

  const unpickedTableData: ItemTableRow[] = useMemo(() => {
    if (fulfillmentGroup.pickStatus !== FulfillmentGroupPickStatus.PROBLEM) {
      return [];
    }

    const expandedItems = expandLineItemsToPickableItems(
      fulfillmentGroup.items,
      {
        hideInputItems:
          team?.settings.outboundLabels?.billOfMaterials
            ?.alwaysHideInputItems ?? false,
      },
    );
    const itemsWithPickedQty = groupProblemPickLineItems({
      items: expandedItems,
      pickedQuantityByExternalId,
      degroupByCustomAttributes: showCustomAttributes,
    }).map(({ item, originalItems, pickedQuantity }) => ({
      ...item,
      pickedQuantity,
      allOriginalItems: originalItems,
    }));

    return itemsWithPickedQty.filter(
      (item) => item.pickedQuantity < item.quantity,
    );
  }, [
    fulfillmentGroup.items,
    fulfillmentGroup.pickStatus,
    pickedQuantityByExternalId,
    showCustomAttributes,
    team?.settings.outboundLabels?.billOfMaterials?.alwaysHideInputItems,
  ]);

  const handleBinQuantityModalClose = useHandler(() => {
    setSelectedItem(null);
    setIsBinQuantityModalOpen(false);
    setQuantityLeftToPick(0);
  });

  const handleMarkAsPicked = useHandler((row: ItemTableRow) => {
    setSelectedItem(row);
    setIsBinQuantityModalOpen(true);
    setQuantityLeftToPick(row.quantity - row.pickedQuantity);
  });

  const [quantityUpdateLoad, triggerQuantityUpdate] = useTriggerLoadWithInput(
    async (
      input: { quantity: number; sources?: ProblemPickSource[] },
      signal,
    ) => {
      if (!selectedItem || !fulfillmentGroup?._id) {
        return;
      }

      try {
        let remainingToPick = input.quantity;
        const updates: {
          fulfillmentOrderLineItemExternalId: string;
          quantityPicked: number;
          sources?: ProblemPickSource[];
        }[] = [];
        let remainingSources = input.sources;

        for (const originalItem of selectedItem.allOriginalItems) {
          if (remainingToPick <= 0) {
            break;
          }

          const currentPicked =
            pickedQuantityByExternalId.get(originalItem.externalId) || 0;
          const itemRemaining = originalItem.quantity - currentPicked;

          if (itemRemaining > 0) {
            const qtyToPickForItem = Math.min(remainingToPick, itemRemaining);
            const newAbsoluteQuantity = currentPicked + qtyToPickForItem;
            const sourceAllocation = remainingSources
              ? takeProblemPickSources({
                  sources: remainingSources,
                  quantity: qtyToPickForItem,
                })
              : null;
            if (sourceAllocation?.missingQuantity) {
              throw new Error(
                "Selected inventory does not match the exact item being picked",
              );
            }
            remainingSources = sourceAllocation?.remainingSources;

            updates.push({
              fulfillmentOrderLineItemExternalId: originalItem.externalId,
              quantityPicked: newAbsoluteQuantity,
              sources: sourceAllocation?.selectedSources,
            });

            remainingToPick -= qtyToPickForItem;
          }
        }

        if (updates.length > 0) {
          const result = await rpcClient.setFulfillmentGroupPickedQuantities(
            { fulfillmentGroupId: fulfillmentGroup._id, updates },
            { signal },
          );

          if (!result.success) {
            toast(result.message || "Failed to update quantity", {
              variant: "error",
              duration: Temporal.Duration.from({ seconds: 3 }),
            });
            return;
          }

          if (result.pickSessionItemStatuses && onFulfillmentGroupUpdated) {
            const updatedGroup = {
              ...fulfillmentGroup,
              pickSessionItemStatuses: result.pickSessionItemStatuses,
            };
            onFulfillmentGroupUpdated(updatedGroup);
          }
        }
      } catch (error) {
        toast("Failed to update quantity", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 3 }),
        });
      } finally {
        handleBinQuantityModalClose();
      }
    },
  );

  const columns: RedoTableColumn<ItemTableRow>[] = useMemo(
    () => [
      createItemColumn(),
      createBinLocationColumn(),
      createMissingColumn(),
      createMarkAsPickedColumn(handleMarkAsPicked, needsToteAssignment),
    ],
    [handleMarkAsPicked, needsToteAssignment],
  );

  const noUnpickedItems = unpickedTableData.length === 0;

  const [setPickStatusLoad, triggerSetPickStatus] = useTriggerLoad(
    async (signal) => {
      try {
        const result = await rpcClient.setFulfillmentGroupPickStatus(
          {
            fulfillmentGroupId: fulfillmentGroup._id,
            pickStatus: FulfillmentGroupPickStatus.PICKED,
          },
          { signal },
        );

        if (!result.success) {
          toast("Failed to mark order as picked", {
            variant: "error",
            duration: Temporal.Duration.from({ seconds: 3 }),
          });
          return;
        }

        if (onFulfillmentGroupUpdated) {
          onFulfillmentGroupUpdated({
            ...fulfillmentGroup,
            pickStatus: FulfillmentGroupPickStatus.PICKED,
          });
        }

        onConfirm();
      } catch (error) {
        toast("Failed to mark order as picked", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 3 }),
        });
      }
    },
  );

  return (
    <>
      <RedoModal
        additionalFooterContent={
          <RedoButton
            hierarchy="tertiary"
            onClick={onViewFullDetails}
            size="sm"
            text="View full order details"
          />
        }
        buttonPlacement="tight"
        isOpen={isOpen}
        modalSize="lg"
        onModalCloseRequested={onClose}
        primaryButton={{
          text: "Mark as picked",
          onClick: triggerSetPickStatus,
          size: "sm",
          disabled: !noUnpickedItems,
          pending: setPickStatusLoad.pending,
          theme: "success",
        }}
        secondaryButton={{ text: "Cancel", onClick: onClose, size: "sm" }}
        theme="error"
        title="Missing items"
        TitleIcon={AlertHexagonIcon}
      >
        <Flex dir="column" gap="lg">
          <Text fontSize="sm" textColor="secondary">
            This order has missing items. Once all of the items are picked this
            order may be fulfilled.
          </Text>

          {needsToteAssignment && (
            <Flex
              align="flex-end"
              bgColor="secondary"
              borderRadius="lg"
              dir="column"
              gap="md"
              p="xl"
            >
              <Text fontSize="sm" fontWeight="medium">
                Assign a tote before marking items as picked
              </Text>
              <Flex align="flex-end" gap="md" w="full">
                <Flex grow="1">
                  <RedoSingleSelectDropdownInput
                    emptyListMessage="No totes available"
                    itemsLoading={availableTotesLoad.pending}
                    options={toteOptions}
                    optionSelected={({ item }) => setSelectedTote(item)}
                    placeholder="Select a tote..."
                    searchPlaceholder="Search totes..."
                    searchString={toteSearchString}
                    selectedItem={selectedTote ?? undefined}
                    setSearchString={setToteSearchString}
                    size="sm"
                  />
                </Flex>
                <RedoButton
                  disabled={!selectedTote}
                  hierarchy="primary"
                  onClick={() => triggerAssignTote()}
                  pending={assignToteLoad.pending}
                  size="sm"
                  text="Assign tote"
                />
              </Flex>
            </Flex>
          )}

          {unpickedTableData.length > 0 && (
            <RedoStandaloneTable
              layout={{ columns, headerColor: "grey" }}
              tableData={unpickedTableData}
            />
          )}

          {noUnpickedItems && (
            <Flex
              align="center"
              bgColor="secondary"
              borderRadius="lg"
              justify="center"
              p="xl"
            >
              <Text fontSize="sm" textColor="secondary">
                All items have been picked.
              </Text>
            </Flex>
          )}
        </Flex>
      </RedoModal>

      {selectedItem && (
        <BinQuantityModal
          isOpen={isBinQuantityModalOpen}
          item={selectedItem}
          locationId={pickSession?.crdbLocationId as LocationId | undefined}
          onClose={handleBinQuantityModalClose}
          onConfirm={(quantity: number, sources?: ProblemPickSource[]) => {
            triggerQuantityUpdate({ quantity, sources });
          }}
          onConfirmLoad={quantityUpdateLoad}
          productId={
            selectedItem.allOriginalItems[0]?.redoProductId as
              | ProductId
              | undefined
          }
          quantityLeftToPick={quantityLeftToPick}
          requiredInventoryItemId={selectedItem.imsInventoryItemId}
          requireSourceBin={requireSourceBin}
          resetScanValue={() => {}}
          scanValue={null}
          team={team}
        />
      )}
    </>
  );
});
